import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject, fromEvent, merge, timer, firstValueFrom } from 'rxjs';
import { debounceTime, filter, map, takeUntil, tap, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private readonly SESSION_TIMEOUT = 8 * 60 * 60 * 1000; // 8 hours
  private readonly WARNING_TIME = 15 * 60 * 1000; // 15 minutes before timeout
  private readonly ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'];
  
  private sessionTimer?: any;
  private warningTimer?: any;
  private lastActivity: number = Date.now();
  private destroy$ = new Subject<void>();
  private sessionWarningShown = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private dialog: MatDialog
  ) {}

  startSessionMonitoring(): void {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    // Reset timers
    this.resetTimers();
    
    // Monitor user activity
    this.setupActivityMonitoring();
    
    // Start session timer
    this.startSessionTimer();
  }

  stopSessionMonitoring(): void {
    this.clearTimers();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupActivityMonitoring(): void {
    // Create observables for user activity events
    const activityEvents$ = merge(
      ...this.ACTIVITY_EVENTS.map(eventType => 
        fromEvent(document, eventType)
      )
    );

    // Debounce activity events to avoid excessive updates
    activityEvents$
      .pipe(
        debounceTime(1000),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.onUserActivity();
      });
  }

  private onUserActivity(): void {
    this.lastActivity = Date.now();
    
    // If warning is shown, close it and reset timers
    if (this.sessionWarningShown) {
      this.dialog.closeAll();
      this.sessionWarningShown = false;
    }
    
    // Reset timers on activity
    this.resetTimers();
    this.startSessionTimer();
  }

  private startSessionTimer(): void {
    // Set warning timer
    this.warningTimer = setTimeout(() => {
      this.showSessionWarning();
    }, this.SESSION_TIMEOUT - this.WARNING_TIME);

    // Set session timeout timer
    this.sessionTimer = setTimeout(() => {
      this.handleSessionTimeout();
    }, this.SESSION_TIMEOUT);
  }

  private showSessionWarning(): void {
    if (this.sessionWarningShown) {
      return;
    }
    
    this.sessionWarningShown = true;
    
    // Import and use a proper dialog component
    import('./session-warning-dialog/session-warning-dialog.component').then(m => {
      const dialogRef = this.dialog.open(m.SessionWarningDialogComponent, {
        width: '400px',
        disableClose: true,
        data: {
          timeRemaining: this.WARNING_TIME / 1000 / 60 // Convert to minutes
        }
      });
      
      dialogRef.afterClosed().subscribe(result => {
        this.sessionWarningShown = false;
        if (result === 'extend') {
          this.extendSession();
        } else {
          this.handleSessionTimeout();
        }
      });
    });
  }

  private async extendSession(): Promise<void> {
    try {
      // Refresh the token to extend the session
      await firstValueFrom(this.authService.refreshToken());

      // Reset activity and timers
      this.lastActivity = Date.now();
      this.resetTimers();
      this.startSessionTimer();
    } catch (error) {
      console.error('Failed to extend session:', error);
      this.handleSessionTimeout();
    }
  }

  private handleSessionTimeout(): void {
    // Clear all timers
    this.clearTimers();
    
    // Logout user
    this.authService.logout();
    
    // Navigate to login with timeout message
    this.router.navigate(['/auth/login'], {
      queryParams: { 
        message: 'Your session has expired. Please login again.',
        returnUrl: this.router.url
      }
    });
  }

  private resetTimers(): void {
    this.clearTimers();
  }

  private clearTimers(): void {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = undefined;
    }
    
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = undefined;
    }
  }

  // Check if session is about to expire
  isSessionExpiringSoon(): boolean {
    const timeSinceLastActivity = Date.now() - this.lastActivity;
    return timeSinceLastActivity > (this.SESSION_TIMEOUT - this.WARNING_TIME);
  }

  // Get remaining session time in milliseconds
  getRemainingSessionTime(): number {
    const timeSinceLastActivity = Date.now() - this.lastActivity;
    return Math.max(0, this.SESSION_TIMEOUT - timeSinceLastActivity);
  }
}